import React, { useState, useEffect } from 'react'
import { Button, Form, InputGroup, Modal } from 'react-bootstrap';
import ToastComponent from '../../Toast/Toast';
import BootstrapTable from 'react-bootstrap-table-next';
import ToolkitProvider from 'react-bootstrap-table2-toolkit';
import { useTranslation } from 'react-i18next';
import Utils from '../../../services/Utils';
import { deleteManagementSignature, generateIvSignAuthStatusUri, generateIvSignAuthUri, getManagementSignature, postManagementSignature, putManagementSignature } from '../../../services/signatureMethods/customServiceSignature';
import LoadingCircle from '../../LoadingCircle';

import plusIcon from '../../../assets/icons/plus-button.png';
import minusIcon from '../../../assets/icons/minus.png';

const IvnosysCertificates = ({ parentState, refreshCertificatesList, children }) => {
    const INITIAL_DATA = {
        username: '',
        password: '',
        organization: ''
    }
    const [data, setData] = useState(INITIAL_DATA);
    const [isAuthorized, setIsAuthorized] = useState(false);
    const [loading, setLoading] = useState(true);
    const [successMFA, setSuccessMFA] = useState(false);
    const [error, setError] = useState(null);

    const [showAuthorizeModal, setShowAuthorizeModal] = useState(false);

    const { username, password, organization } = data;

    const { t } = useTranslation();
    const certsNotAvailable = <p>{t('certificates.certificate-not-available')}</p>;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setData({
            ...data,
            [name]: value
        });
    };

    const {
        certificatesOrdered: { Ivnosys },
    } = parentState;

    const requestAuthorizeCallback = (response) => {
        const { RedirectUrl } = response.data;
        setData(INITIAL_DATA);

        if (RedirectUrl) {
            // Save important data from sessionStorage to localStorage
            localStorage.setItem('AccessMethod', sessionStorage.getItem('AccessMethod'));
            localStorage.setItem('oauthTokenB64', sessionStorage.getItem('oauthTokenB64'));
            localStorage.setItem('appearanceConf', sessionStorage.getItem('appearanceConf'));
            localStorage.setItem('showApprovals', sessionStorage.getItem('showApprovals'));
            localStorage.setItem('user', sessionStorage.getItem('user'));

            window.location.href = RedirectUrl;
            
            return;
        }
        setSuccessMFA(true);

        checkIsIvnosysAuthorized();
        refreshCertificatesList();
    };

    const checkIsIvnosysAuthorized = () => {
        const token = sessionStorage.getItem('oauthTokenB64');
        const vidMobileUserGuid = Utils.getViDMobileUserGUIDFromOauthToken(token);
        setLoading(true);
        getManagementSignature(generateIvSignAuthStatusUri(vidMobileUserGuid), '', token)
        .then((response) => {
            const { data } = response;
            const { IsAuthorized } = data;

            setIsAuthorized(IsAuthorized);    
        })
        .finally(() => {
            setLoading(false);
        })
    }

    const handleIvnosysAuthorize = () => {
        setShowAuthorizeModal(false);
        const body = {
            Username: username,
            Password: password,
            Organization: organization
        };

        setLoading(true);

        const vidMobileUserGuid = Utils.getViDMobileUserGUIDFromOauthToken(parentState.oauthTokenB64);

        postManagementSignature(generateIvSignAuthUri(vidMobileUserGuid), body, parentState.oauthTokenB64)
        .then(requestAuthorizeCallback)
        .catch((error) => {
            setError(error.response.data);
        })
        .finally(() => {
            setLoading(false);
        })
    }
    
    const handleIvnosysUnauthorize = () => {
        setLoading(true);

        const vidMobileUserGuid = Utils.getViDMobileUserGUIDFromOauthToken(parentState.oauthTokenB64);

        deleteManagementSignature(generateIvSignAuthUri(vidMobileUserGuid), {}, parentState.oauthTokenB64)
        .then(() => {
            setData(INITIAL_DATA);
            setIsAuthorized(false);
        })
        .catch((error) => {
            console.error('Error authorizing Ivnosys:', error);
        })
        .finally(() => {
            setLoading(false);
        })
    }
    
    const handleIvnosysSynchronize = () => {
        setLoading(true);

        const vidMobileUserGuid = Utils.getViDMobileUserGUIDFromOauthToken(parentState.oauthTokenB64);

        putManagementSignature(generateIvSignAuthUri(vidMobileUserGuid), {}, parentState.oauthTokenB64)
        .then(requestAuthorizeCallback)
        .catch((error) => {
            console.error('Error authorizing Ivnosys:', error);
        })
        .finally(() => {
            setLoading(false);
        })
    }

    let ivnosysColumns = [
    {
        dataField: 'certalias',
        text: t('certificates.certificate'),
        align: 'left',
        sort: true,
        headerAlign: 'left',
        headerClasses: 'headerClass1',
        style: { verticalAlign: 'middle' }
    },
    {
        dataField: 'ExpirationDate',
        text: t('certificates.certificate-date'),
        align: 'left',
        sort: true,
        headerAlign: 'left',
        headerClasses: 'headerClass2',
        style: { verticalAlign: 'middle' }
    }
    ];

    if (Ivnosys[0]?.DN) {
        ivnosysColumns[0] = {
            dataField: 'DN',
            text: t('certificates.certificate'),
            align: 'left',
            sort: true,
            headerAlign: 'left',
            headerClasses: 'headerClass1',
            style: { verticalAlign: 'middle' }
        }
    }

    if (Ivnosys.length === 0) {
      Ivnosys.push({
        GUID: 'placeholder',
        certalias: '---',
        ExpirationDate: '---',
        DN: '---',
      })
    }

    useEffect(() => {
        checkIsIvnosysAuthorized();

        const params = new URLSearchParams(window.location.search);
        const ivnosysAuthorization = params.get('ivnosysAuthorization');

        if (ivnosysAuthorization) {
            // show popup to inform the user that the authorization was successful
            setSuccessMFA(true);
        }
    }, []);

    return (
      <>
        <div className="table-container">
          <div className='table-container-header'>
            {isAuthorized ? (
              <button
                className="new-create-button"
                onClick={handleIvnosysUnauthorize}
              >
                <img src={minusIcon} alt="minus icon" />
                <span>{t("general.unauthorize")}</span>
              </button>
            ) : (
              <button
                className="new-create-button"
                onClick={() => setShowAuthorizeModal(true)}
              >
                <img src={plusIcon} alt="plus icon" />
                <span>{t("general.authorize")}</span>
              </button>
            )}
            {isAuthorized && children}
          </div>
          {loading ? (
            <LoadingCircle />
          ) : (
            <>
              {isAuthorized ? (
                <>
                  {Ivnosys.length === 0 && certsNotAvailable}
                  {Ivnosys.length > 0 && (
                    <ToolkitProvider
                      keyField="GUID"
                      data={Ivnosys}
                      columns={ivnosysColumns}
                    >
                      {(props) => (
                        <div>
                          <BootstrapTable striped {...props.baseProps} />
                        </div>
                      )}
                    </ToolkitProvider>
                  )}
                  <div className="rt-buttons-container">
                    <Button
                      type="button"
                      id="synchronizeRedTrust"
                      className="btn btn-primary signature-image ml-3"
                      onClick={handleIvnosysSynchronize}
                    >
                      {t("general.synchronize")}
                    </Button>
                  </div>
                </>
              ) : (
                <></>
              )}
            </>
          )}

          <Modal
            show={showAuthorizeModal}
            onHide={() => setShowAuthorizeModal(false)}
          >
            <Modal.Header closeButton>
              <Modal.Title>{t("general.authorize")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <InputGroup className="mb-3">
                <InputGroup.Prepend>
                  <InputGroup.Text id="ivnosys-user">
                    {t("general.user")}
                  </InputGroup.Text>
                </InputGroup.Prepend>
                <Form.Control
                  required
                  type="text"
                  aria-label="Default"
                  aria-describedby="ivnosys-user"
                  name="username"
                  value={username}
                  onChange={handleChange}
                />
              </InputGroup>
              <InputGroup className="mb-3">
                <InputGroup.Prepend>
                  <InputGroup.Text id="ivnosys-password">
                    {t("general.password")}
                  </InputGroup.Text>
                </InputGroup.Prepend>
                <Form.Control
                  required
                  type="password"
                  name="password"
                  aria-label="Default"
                  aria-describedby="ivnosys-password"
                  value={password}
                  onChange={handleChange}
                />
              </InputGroup>
              <InputGroup className="mb-3">
                <InputGroup.Prepend>
                  <InputGroup.Text id="ivnosys-organization">
                    {t("general.organization")}
                  </InputGroup.Text>
                </InputGroup.Prepend>
                <Form.Control
                  required
                  type="text"
                  name="organization"
                  aria-label="Default"
                  aria-describedby="ivnosys-organization"
                  value={organization}
                  onChange={handleChange}
                />
              </InputGroup>
            </Modal.Body>
            <Modal.Footer>
              <Button
                type="button"
                id="authorizeIv"
                className="btn btn-primary signature-image"
                onClick={handleIvnosysAuthorize}
              >
                {t("general.confirm")}
              </Button>
            </Modal.Footer>
          </Modal>
        </div>
        <ToastComponent
          onClose={() => setSuccessMFA(false)}
          show={successMFA}
          autohide
          delay={3000}
          title={t('certificates.task-completed-text')}
          message={t('certificates.authorized-success-ivonsys')}
          variant="success"
          zIndex={10}
        />
        <ToastComponent
          onClose={() => setError(null)}
          show={!!error}
          title={t('general.error')}
          message={error}
          variant="error"
          closeButton
          zIndex={11}
        />
      </>
    );
}

export default IvnosysCertificates
